import { GoogleGenerativeAI, GenerativeModel } from "@google/generative-ai";
import { ChatCompletionRequestMessage } from "openai";
import { ProviderSettings, Settings } from "../db";
import { Provider, ProviderModel, providerRegistry } from "../utils/providers";

// Define initial Google models
const googleModels: ProviderModel[] = [
  {
    id: "gemini-pro",
    name: "Gemini Pro",
    description: "Balanced model for most tasks",
    contextWindow: 32768,
    maxTokens: 8192,
  },
  {
    id: "gemini-pro-vision",
    name: "Gemini Pro Vision",
    description: "Model with vision capabilities",
    contextWindow: 16384,
    maxTokens: 4096,
  },
  {
    id: "gemini-ultra",
    name: "Gemini Ultra",
    description: "Most powerful Gemini model",
    contextWindow: 32768,
    maxTokens: 8192,
  },
];

// Helper function to get provider settings from the main settings object
function getProviderSettings(settings: Settings): ProviderSettings {
  const providerSettings = settings.providerSettings?.[settings.provider || 'google'];
  if (!providerSettings) {
    throw new Error("Provider settings not found");
  }
  return providerSettings;
}

// Helper function to get Google client
function getGoogleClient(settings: Settings): GoogleGenerativeAI {
  const providerSettings = getProviderSettings(settings);
  
  if (!providerSettings.apiKey) {
    throw new Error("API Key is required");
  }
  
  return new GoogleGenerativeAI(providerSettings.apiKey);
}

// Helper function to get a specific model
function getGoogleModel(client: GoogleGenerativeAI, modelName: string): GenerativeModel {
  return client.getGenerativeModel({ model: modelName });
}

// Helper function to map OpenAI messages to Google format
function mapMessagesToGoogle(messages: ChatCompletionRequestMessage[]): any[] {
  const googleMessages: any[] = [];
  let currentRole: string | null = null;
  let currentContent: string[] = [];

  // Process messages in order
  for (const msg of messages) {
    // Skip system messages as Google doesn't have a direct equivalent
    if (msg.role === "system") continue;

    if (msg.role !== currentRole && currentRole !== null) {
      // Role changed, push the accumulated content
      googleMessages.push({
        role: currentRole === "assistant" ? "model" : "user",
        parts: [{ text: currentContent.join("\n") }],
      });
      currentContent = [];
    }

    currentRole = msg.role;
    currentContent.push(msg.content);
  }

  // Push the last accumulated content if any
  if (currentRole !== null && currentContent.length > 0) {
    googleMessages.push({
      role: currentRole === "assistant" ? "model" : "user",
      parts: [{ text: currentContent.join("\n") }],
    });
  }

  return googleMessages;
}

// Create the Google provider
const googleProvider: Provider = {
  id: "google",
  name: "Google",
  description: "Gemini AI models by Google",
  authType: "api-key",
  apiKeyUrl: "https://makersuite.google.com/app/apikey",
  defaultModel: "gemini-pro",
  models: googleModels,

  // Fetch models from Google
  async fetchModels(settings: Settings): Promise<ProviderModel[]> {
    try {
      const providerSettings = getProviderSettings(settings);
      
      if (!providerSettings.apiKey) {
        console.warn("Google API key not set, using default model list");
        return googleModels;
      }
      
      const client = getGoogleClient(settings);
            
      // Google's Generative AI SDK doesn't have a direct method to list all available models
      // We'll check if some known models are accessible to the user's API key
      // by making lightweight calls to the API
      
      // Define known Google models to test for availability
      const knownModelIds = [
        "gemini-pro",
        "gemini-pro-vision",
        "gemini-ultra",
        "gemini-1.5-pro",
        "gemini-1.5-flash",
      ];
      
      const availableModels: ProviderModel[] = [];
      
      // Try to access each model with a minimal API call
      for (const modelId of knownModelIds) {
        try {
          const model = client.getGenerativeModel({ model: modelId });
          
          // Try a simple countTokens call - lightweight way to check if the model is accessible
          await model.countTokens("test");
          
          // If we get here, the model is available to this API key
          console.log(`Model ${modelId} is available`);
          
          // Format the model name nicely
          const formattedName = modelId
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          
          // Add to available models
          availableModels.push({
            id: modelId,
            name: formattedName,
            description: `Google ${formattedName} model`,
            contextWindow: modelId.includes('ultra') ? 32768 : 
                          modelId.includes('1.5-pro') ? 1000000 :
                          modelId.includes('1.5-flash') ? 1000000 : 
                          32768,
            maxTokens: 8192,
          });
        } catch (error) {
          // This model is not available to this API key
          console.log(`Model ${modelId} is not available: ${error}`);
        }
      }
      
      // If we found available models, return them
      if (availableModels.length > 0) {
        console.log(`Found ${availableModels.length} available Google models`);
        return availableModels;
      }
      
      // If no models were found available, return the default list
      console.log("No models found available, using default Google models list");
      return googleModels;
    } catch (error: any) {
      console.error("Error fetching Google models:", error);
      return googleModels;
    }
  },

  // Validate credentials
  async validateCredentials(settings: Settings): Promise<boolean> {
    try {
      const providerSettings = getProviderSettings(settings);
      
      if (!providerSettings.apiKey) {
        throw new Error("API Key is required");
      }

      const client = getGoogleClient(settings);
      const model = getGoogleModel(client, "gemini-pro");

      // Simple validation by counting tokens
      await model.countTokens({ contents: [{ role: "user", parts: [{ text: "Hello" }] }] });

      return true;
    } catch (error: any) {
      console.error("Google validation error:", error);
      const message = error.message;
      throw new Error(`Google API Key validation failed: ${message}`);
    }
  },

  // Create chat completion
  async createChatCompletion(
    settings: Settings,
    messages: ChatCompletionRequestMessage[]
  ): Promise<any> {
    try {
      const providerSettings = getProviderSettings(settings);
      
      if (!providerSettings.apiKey) {
        throw new Error("API Key is required");
      }

      if (!settings.model) {
        throw new Error("Model is not configured");
      }

      const client = getGoogleClient(settings);
      const model = getGoogleModel(client, settings.model);
      const googleMessages = mapMessagesToGoogle(messages);

      if (googleMessages.length === 0) {
        throw new Error("No valid messages found for Google API call");
      }

      // Get token count for usage statistics
      const tokenCount = await model.countTokens({ contents: googleMessages });
      
      // Generate content
      const result = await model.generateContent({
        contents: googleMessages,
        generationConfig: {
          maxOutputTokens: 4096,
        },
      });

      const response = result.response;
      const text = response.text();

      // Convert Google response to OpenAI-like format for compatibility
      return {
        id: `google-${Date.now()}`,
        object: "chat.completion",
        created: Math.floor(Date.now() / 1000),
        model: settings.model,
        choices: [
          {
            index: 0,
            message: {
              role: "assistant",
              content: text,
            },
            finish_reason: "stop",
          },
        ],
        usage: {
          prompt_tokens: tokenCount.totalTokens,
          completion_tokens: Math.floor(text.length / 4), // Rough estimate
          total_tokens: tokenCount.totalTokens + Math.floor(text.length / 4),
        },
      };
    } catch (error: any) {
      console.error("Google chat completion error:", error);
      const message = error.message;
      throw new Error(`Google chat completion failed: ${message}`);
    }
  },

  // Create streaming chat completion
  async createStreamChatCompletion(
    settings: Settings,
    messages: ChatCompletionRequestMessage[],
    chatId: string,
    messageId: string
  ): Promise<any> {
    try {
      // We need to import db here to avoid circular dependency
      const { db } = await import("../db");

      const providerSettings = getProviderSettings(settings);
      
      if (!providerSettings.apiKey) {
        throw new Error("API Key is required");
      }

      if (!settings.model) {
        throw new Error("Model is not configured");
      }

      const client = getGoogleClient(settings);
      const model = getGoogleModel(client, settings.model);
      const googleMessages = mapMessagesToGoogle(messages);

      if (googleMessages.length === 0) {
        throw new Error("No valid messages found for Google API call");
      }

      // Get token count for usage statistics
      const tokenCount = await model.countTokens({ contents: googleMessages });
      
      // Initialize with empty content
      db.messages.update(messageId, { content: "" });
      
      // Generate content with streaming
      const result = await model.generateContentStream({
        contents: googleMessages,
        generationConfig: {
          maxOutputTokens: 4096,
        },
      });

      let fullText = "";
      
      // Process the stream
      for await (const chunk of result.stream) {
        const chunkText = chunk.text();
        fullText += chunkText;
        
        // Update the message with the accumulated text
        db.messages.update(messageId, { content: fullText + "█" });
      }
      
      // Update the message with the final text
      db.messages.update(messageId, { content: fullText });
      
      // Update token count
      const completionTokens = Math.floor(fullText.length / 4); // Rough estimate
      const totalTokens = tokenCount.totalTokens + completionTokens;
      
      db.chats.where({ id: chatId }).modify((chat: any) => {
        if (chat.totalTokens) {
          chat.totalTokens += totalTokens;
        } else {
          chat.totalTokens = totalTokens;
        }
      });
      
      return result;
    } catch (error: any) {
      console.error("Google streaming error:", error);
      const message = error.message;
      
      // We need to import db here to avoid circular dependency
      const { db } = await import("../db");
      db.messages.update(messageId, { content: `Error: ${message}` });
      
      throw new Error(`Google streaming failed: ${message}`);
    }
  },
};

// Register the Google provider
providerRegistry.register(googleProvider);

export default googleProvider;
